/**
 * Mask token characters: `9` matches a digit, `a` matches a letter, `*` matches either.
 * Any other character in a mask is a literal that is auto-inserted while typing.
 */
const TOKEN_MATCHERS: Record<string, RegExp> = {
	'9': /[0-9]/,
	a: /[a-zA-Z]/,
	'*': /[a-zA-Z0-9]/,
};

/** Returns true if the given mask character is a token slot (`9`/`a`/`*`), false for literals. */
export const isMaskToken = (char: string): boolean => char in TOKEN_MATCHERS;

/** Number of fillable (token) positions in the mask. */
export const countMaskTokens = (mask: string): number => {
	return mask.split('').filter(isMaskToken).length;
};

/**
 * Strips an arbitrary input string down to only the characters that can fill the mask's
 * token positions, in order. Literals and junk characters are dropped; extraction stops
 * once every token position could be filled.
 */
export const extractRawValue = (input: string, mask: string): string => {
	const tokens = mask.split('').filter(isMaskToken);
	let raw = '';
	for (const char of input) {
		if (raw.length >= tokens.length) {
			break;
		}
		if (TOKEN_MATCHERS[tokens[raw.length]].test(char)) {
			raw += char;
		}
	}
	return raw;
};

// ─── Placeholder-slot helpers ─────────────────────────────────────────────────

/**
 * Returns true if `char` satisfies the token type represented by `maskChar`.
 * Returns false for non-token mask characters (literals).
 */
export const matchesToken = (char: string, maskChar: string): boolean => {
	const matcher = TOKEN_MATCHERS[maskChar];
	return matcher !== undefined && matcher.test(char);
};

/**
 * Produces the normalized full-length slotted string from an arbitrary `value`.
 * The result is always `mask.length` characters long.
 *
 * When `value.length === mask.length` (already a full-length slotted string), each token
 * slot is read positionally from `value` — this preserves interior placeholder holes.
 * When `value.length < mask.length`, token characters are extracted left-to-right via
 * `extractRawValue` and filled into slots from the left.
 *
 * Literal positions always come from `mask`, regardless of what `value` has there.
 */
export const buildSlotString = (value: string, mask: string, placeholderChar: string): string => {
	const chars = mask.split('');

	if (value.length === mask.length) {
		// Positional copy: read each token slot from value directly, preserve placeholders
		return chars
			.map((maskChar, i) => {
				if (!isMaskToken(maskChar)) {
					return maskChar; // always literal from mask
				}
				const v = value[i];
				// If the value char matches the token or is the placeholder, keep it; otherwise use placeholder
				if (v === placeholderChar || matchesToken(v, maskChar)) {
					return v;
				}
				return placeholderChar;
			})
			.join('');
	}

	// Shorter value: extract raw token chars and fill left-to-right
	const raw = extractRawValue(value, mask);
	let rawIndex = 0;
	return chars
		.map(maskChar => {
			if (!isMaskToken(maskChar)) {
				return maskChar;
			}
			if (rawIndex < raw.length) {
				return raw[rawIndex++];
			}
			return placeholderChar;
		})
		.join('');
};

/**
 * Returns `''` when all token slots in `slotString` contain `placeholderChar`
 * (meaning the field is visually empty and the HTML `placeholder` should show).
 * Otherwise returns `slotString` unchanged.
 */
export const slotStringToValue = (slotString: string, mask: string, placeholderChar: string): string => {
	for (let i = 0; i < mask.length; i++) {
		if (isMaskToken(mask[i]) && slotString[i] !== placeholderChar) {
			return slotString;
		}
	}
	return '';
};

/**
 * Returns the smallest token index strictly greater than `fromExclusive`,
 * or `mask.length` if no such index exists.
 */
export const nextTokenIndex = (mask: string, fromExclusive: number): number => {
	for (let i = fromExclusive + 1; i < mask.length; i++) {
		if (isMaskToken(mask[i])) {
			return i;
		}
	}
	return mask.length;
};

/**
 * Returns the largest token index strictly less than `fromExclusive`,
 * or `-1` if no such index exists.
 */
export const prevTokenIndex = (mask: string, fromExclusive: number): number => {
	for (let i = fromExclusive - 1; i >= 0; i--) {
		if (isMaskToken(mask[i])) {
			return i;
		}
	}
	return -1;
};

/**
 * Returns the smallest token index `>= pos`, or `-1` if none exists at or after `pos`.
 */
export const firstTokenIndexAtOrAfter = (mask: string, pos: number): number => {
	for (let i = pos; i < mask.length; i++) {
		if (isMaskToken(mask[i])) {
			return i;
		}
	}
	return -1;
};
